/**
 * TemplateStyler — internal editor tool.
 * BRAND (logo → analysis → confirm) → RESTYLE the 3 master Creatomate JSONs → COPY → Creatomate.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Palette, Layers, BookMarked, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import StylerBrandPanel from "./StylerBrandPanel";
import StylerOutput from "./StylerOutput";
import StylerMasters from "./StylerMasters";
import StylerAccess from "./StylerAccess";
import StylerBrands from "./StylerBrands";
import "./TemplateStyler.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EMPTY_BRAND = {
  id: null, name: "", logo_url: null, logo_preview: null, palette: [],
  primary: "#102D52", accent: "#F47770", secondary: "#FFFFFF", neutral: "#FFFFFF",
  hero_font: "Cormorant Garamond", support_font: "Montserrat",
  detected: null, typography: null,
};

export default function TemplateStyler() {
  const [view, setView] = useState("styler");
  const [access, setAccess] = useState(null);
  const [fonts, setFonts] = useState([]);
  const [brand, setBrand] = useState(EMPTY_BRAND);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [autoFix, setAutoFix] = useState(true);
  const [cardOpacity, setCardOpacity] = useState(80); // % — solid colour cards let the video read through; logo card stays 100%

  useEffect(() => {
    (async () => {
      try {
        const a = await axios.get(`${API}/template-styler/access`);
        setAccess(a.data);
        if (a.data.allowed) {
          const f = await axios.get(`${API}/template-styler/fonts`);
          setFonts(f.data.fonts || []);
        }
      } catch (_) {
        setAccess({ allowed: false, can_manage_access: false });
      }
    })();
  }, []);

  const generate = useCallback(async (b = brand) => {
    setGenerating(true);
    try {
      const payload = {
        brand: {
          name: b.name || "", logo_url: b.logo_url || null, primary: b.primary, accent: b.accent,
          secondary: b.secondary || null, neutral: b.neutral, hero_font: b.hero_font, support_font: b.support_font,
          palette: b.palette || null,
        },
        brand_id: b.id || null,
        auto_fix_contrast: autoFix,
        card_opacity: cardOpacity / 100,
      };
      const r = await axios.post(`${API}/template-styler/generate`, payload);
      setResult(r.data);
      if (r.data.all_valid) toast.success("Three branded templates ready");
      else toast.error("Template generation failed validation — see the flagged tab");
      setTimeout(() => document.getElementById("styler-output")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't generate templates");
    } finally {
      setGenerating(false);
    }
  }, [brand, autoFix, cardOpacity]);

  if (!access) return <div className="ts-loading">Loading Template Styler…</div>;
  if (!access.allowed) {
    return (
      <div className="ts-locked" data-testid="styler-locked">
        <ShieldCheck size={28} />
        <h3>Template Styler is invite-only</h3>
        <p>Ask Luan or a NicheCom super admin to enable it for your editor account.</p>
      </div>
    );
  }

  const NAV = [
    { key: "styler", label: "Styler", icon: Sparkles },
    { key: "brands", label: "Brands", icon: BookMarked },
    { key: "masters", label: "Master Templates", icon: Layers },
    ...(access.can_manage_access ? [{ key: "access", label: "Access", icon: ShieldCheck }] : []),
  ];

  return (
    <div className="ts" data-testid="template-styler">
      <header className="ts-hero">
        <div className="ts-hero-icon"><Palette size={22} /></div>
        <div className="ts-hero-text">
          <h2>Template Styler</h2>
          <p>Upload an agent's logo → confirm the brand → copy three restyled Creatomate templates.</p>
        </div>
        <nav className="ts-nav" data-testid="styler-nav">
          {NAV.map((n) => (
            <button key={n.key} className={`ts-nav-btn ${view === n.key ? "active" : ""}`} onClick={() => setView(n.key)} data-testid={`styler-nav-${n.key}`}>
              <n.icon size={14} /> {n.label}
            </button>
          ))}
        </nav>
      </header>

      {view === "styler" && (
        <>
          <StylerBrandPanel
            brand={brand}
            setBrand={setBrand}
            fonts={fonts}
            onGenerate={() => generate()}
            generating={generating}
            autoFix={autoFix}
            setAutoFix={setAutoFix}
            cardOpacity={cardOpacity}
            setCardOpacity={setCardOpacity}
            onSaved={(saved) => setBrand((b) => ({ ...b, id: saved.id, name: saved.name }))}
          />
          {result && (
            <StylerOutput result={result} onRegenerate={() => generate()} generating={generating} brand={brand} />
          )}
        </>
      )}
      {view === "brands" && (
        <StylerBrands
          onUse={(b) => {
            setBrand({ ...EMPTY_BRAND, ...b, logo_preview: b.logo_url, detected: null, typography: null, palette: b.palette || [] });
            setResult(null);
            setView("styler");
            toast.success(`Loaded ${b.name}`);
          }}
        />
      )}
      {view === "masters" && <StylerMasters />}
      {view === "access" && access.can_manage_access && <StylerAccess />}
    </div>
  );
}
